import React from "react";
import { AiOutlineRight } from "react-icons/ai";

// Bounded category section: anchor target for the sticky nav (scroll-mt keeps
// the heading clear of the sticky bar) + a heading row above its content.
// `accentColor` (hex, inline style — not a Tailwind class) gives each
// category a consistent identity color across its label bar and its cards.
export const SectionShell = ({
  id,
  title,
  subtitle,
  extra,
  accentColor = "#09617E",
  children,
}) => (
  <section id={id} className="k360-section">
    <div className="k360-section-head flex justify-between items-end px-1">
      <div className="flex items-center" style={{ gap: "0.85rem" }}>
        <span
          className="w-1.5 h-7 rounded-full inline-block"
          style={{ backgroundColor: accentColor }}
        />
        <div>
          <h2 className="text-lg font-bold text-slate-800 leading-tight">
            {title}
          </h2>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {extra}
    </div>
    {children}
  </section>
);

export const Card = ({ className = "", accentColor, children }) => (
  <div
    style={accentColor ? { borderTop: `4px solid ${accentColor}` } : undefined}
    className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-100 h-full ${className}`}
  >
    {children}
  </div>
);

export const CardTitle = ({ title, subtitle, extra }) => (
  <div className="flex justify-between items-start mb-4">
    <div>
      <h3 className="text-base font-bold text-slate-800">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {extra}
  </div>
);

// `accentColor` is an optional hex color for a top border stripe, applied as
// an inline style (not a Tailwind class) so it always renders regardless of
// whether this exact utility combination has been JIT-generated yet.
// `compact` trims padding/type scale for grids that need to fit more tiles
// in one row (e.g. 5-up) without wrapping to a second row on typical widths.
export const KpiStatTile = ({
  title,
  value,
  caption,
  icon,
  chipClass,
  captionClass,
  accentColor,
  onClick,
  compact,
}) => (
  <div
    onClick={onClick}
    style={
      accentColor
        ? { borderTop: `4px solid ${accentColor}` }
        : undefined
    }
    className={`bg-white rounded-2xl shadow-sm border border-slate-100 transition-all duration-300 ${
      compact ? "p-3" : "p-5"
    } ${
      onClick
        ? "cursor-pointer hover:shadow-md hover:border-slate-200 transform hover:-translate-y-1"
        : ""
    }`}
  >
    <div className={`flex justify-between items-center ${compact ? "mb-1.5" : "mb-3"}`}>
      <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
        {title}
      </span>
      <div
        className={`${compact ? "p-1" : "p-1.5"} rounded-lg ${chipClass}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {icon}
      </div>
    </div>
    <h3 className={`font-bold text-slate-800 ${compact ? "text-lg" : "text-2xl"}`}>{value}</h3>
    <div className="flex items-center justify-between mt-1">
      <p className={`text-xs ${captionClass || "text-slate-400"}`}>{caption}</p>
      {onClick && <AiOutlineRight className="text-slate-300" size={12} />}
    </div>
  </div>
);

export const TrendKpiCard = ({
  icon,
  iconBg,
  iconColor,
  accentColor,
  value,
  label,
  caption,
  trend,
  onClick,
}) => {
  const trendUp = trend != null && trend >= 0;
  return (
    <div
      onClick={onClick}
      style={{
        borderTop: `4px solid ${accentColor}`,
        minHeight: "190px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      className={`bg-white rounded-2xl p-6 shadow-sm border border-slate-100 transition-all duration-300 ${
        onClick
          ? "cursor-pointer hover:shadow-md hover:-translate-y-1"
          : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center p-2"
          style={{ backgroundColor: iconBg, color: iconColor }}
        >
          {icon}
        </div>
        {trend != null && (
          <span
            className="text-xs font-bold px-4 py-1 rounded-full inline-flex items-center gap-1"
            style={{
              backgroundColor: trendUp ? "#dcfce7" : "#fee2e2",
              color: trendUp ? "#15803d" : "#b91c1c",
            }}
          >
            {trendUp ? "↗" : "↘"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div>
        <div className="text-4xl font-bold text-slate-900 mb-1.5">{value}</div>
        <div
          className="text-xs font-bold uppercase tracking-wide"
          style={{ color: accentColor }}
        >
          {label}
        </div>
      </div>
      {caption && <div className="text-xs text-slate-400 leading-relaxed">{caption}</div>}
    </div>
  );
};

export const StatMini = ({ value, label, bgClass, textClass }) => (
  <div className={`rounded-xl p-3 text-center ${bgClass}`}>
    <div className={`text-xl font-bold ${textClass}`}>{value}</div>
    <div className="text-xs text-slate-500 font-semibold">{label}</div>
  </div>
);

// Deep-link row used by Action Center and section footers. `iconBg`/`iconColor`
// are inline-style hex values (not Tailwind classes) for guaranteed rendering,
// matching the same pattern as TrendKpiCard's icon chip.
export const LinkRow = ({
  icon,
  iconBg = "#f1f5f9",
  iconColor = "#475569",
  label,
  caption,
  right,
  onClick,
}) => (
  <div
    onClick={onClick}
    className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm cursor-pointer transition-all"
  >
    <div className="flex items-center min-w-0" style={{ gap: "1.1rem" }}>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-700 truncate">{label}</div>
        {caption && (
          <div className="text-xs text-slate-400 mt-0.5 truncate">{caption}</div>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0 pl-2">
      {right}
      <AiOutlineRight className="text-slate-300" size={16} />
    </div>
  </div>
);
